// ─── Breakin' Bread Guided Tour Step Definitions ──────────────────────────────────
// Each step can target a DOM element (by data-tour="id") or float in the center.

export interface TourStep {
  id: string;
  title: string;
  description: string;
  /** data-tour attribute value, or null for centre overlay */
  target: string | null;
  /** Where the tooltip appears relative to the target */
  position: "top" | "bottom" | "left" | "right" | "center";
  /** Emoji icon shown in the tooltip header */
  icon: string;
  /** Page the feature lives on (used in Help tab) */
  page: string;
  /** CSS animation class applied to the spotlight ring */
  animation: "pulse" | "bounce" | "glow" | "none";
  /** Optional inline tip shown in the Help tab reference */
  tip?: string;
  /** Optional call-to-action button rendered inside the tooltip (e.g. Sign In) */
  cta?: { label: string; href: string };
}

export const TOUR_STEPS: TourStep[] = [
  {
    id: "welcome",
    title: "Welcome to Breakin' Bread 🎵",
    description:
      "Breakin' Bread is your all-in-one music platform — discover new artists, manage your catalogue, sell in the marketplace, and connect with the community. This quick tour will show you around the key sections.",
    target: null,
    position: "center",
    icon: "🎵",
    page: "overview",
    animation: "none",
  },
  {
    id: "sidebar",
    title: "Sidebar Navigation",
    description:
      "The sidebar on the left is your main navigation hub. Hover icons to see labels, or click to jump to any section — Home, Artists, Music, Marketplace, Community, Radio, and more.",
    target: "tour-sidebar",
    position: "right",
    icon: "🧭",
    page: "everywhere",
    animation: "pulse",
    tip: "Click any icon to navigate. Admin users see extra sections like the Super Admin panel.",
  },
  {
    id: "home-feed",
    title: "Home Feed",
    description:
      "The home page surfaces featured artists, releases, trending tracks, upcoming events, and community picks — all personalised to your taste. Use the filter tabs to jump between categories.",
    target: "tour-home-feed",
    position: "bottom",
    icon: "🏠",
    page: "/home",
    animation: "pulse",
    tip: "You can reorder and show/hide sections on the home page. Look for the Sort & Filter options.",
  },
  {
    id: "media-player",
    title: "Media Player",
    description:
      "The global media player sits at the bottom of the screen. Hit ▶ on any track to play it. The queue, lyrics, waveform, and timeline comments are all available in the expanded player view.",
    target: "tour-media-player",
    position: "top",
    icon: "🎧",
    page: "everywhere",
    animation: "glow",
    tip: "Click the album art or track name to open the full player. Use ⇄ to shuffle and ↺ to repeat.",
  },
  {
    id: "favorites-bar",
    title: "Favourites Bar",
    description:
      "Your favourited tracks, releases, artists, and videos appear in the scrollable bar at the top — perfect for quick access. Drag to scroll, or tap ♥ on anything to save it.",
    target: "tour-favorites-bar",
    position: "bottom",
    icon: "❤️",
    page: "/home",
    animation: "pulse",
    tip: "Favourites sync across devices via Firestore.",
  },
  {
    id: "audio-meters",
    title: "Audio Meters",
    description:
      "The thin VU meter on the right edge shows real-time audio levels while music plays. Click it to open the full visualiser popup with Spectrum, Oscilloscope, Spectrogram, and Goniometer panels — all resizable.",
    target: "tour-vu-strip",
    position: "left",
    icon: "📊",
    page: "everywhere",
    animation: "glow",
    tip: "Drag the dividers between meter panels to resize them. Toggle each meter on/off with the buttons at the top.",
  },
  {
    id: "artists",
    title: "Discover Artists",
    description:
      "Browse the full artists directory. Filter by genre, sort A-Z or by popularity, and switch between grid and list views. Each artist page shows their releases, tracks, videos, a gallery, and curated web links.",
    target: null,
    position: "center",
    icon: "🎤",
    page: "/artists",
    animation: "none",
    tip: "Use the search bar and genre filter chips at the top of the Artists page.",
  },
  {
    id: "music-page",
    title: "Music & Track Pages",
    description:
      "Every track and release has a dedicated page with artwork, lyrics, waveform, credits, related tracks, and SoundCloud-style timeline comments. Download options and PWYW pricing appear when enabled.",
    target: null,
    position: "center",
    icon: "🎼",
    page: "/music/*",
    animation: "none",
    tip: "Timeline comments snap to the playback position — click the waveform to scrub and leave a timestamped comment.",
  },
  {
    id: "media-vault",
    title: "Media Vault",
    description:
      "The Media Vault is your personal upload & management hub. Upload singles or full albums, edit metadata, set pricing, manage contributors, and see all your tracks in one place. Bulk-select rows to edit or delete many at once.",
    target: null,
    position: "center",
    icon: "🗂️",
    page: "/media",
    animation: "none",
    tip: "Use the Audio Editor (🎛️ button) to trim, fade, and export individual tracks.",
  },
  {
    id: "marketplace",
    title: "Marketplace",
    description:
      "Buy and sell digital downloads, beats, samples, merchandise, and more. Items support fixed pricing or Name Your Price (PWYW). Use the grid/list/compact view toggle and sort by price or name.",
    target: null,
    position: "center",
    icon: "🛍️",
    page: "/marketplace",
    animation: "none",
    tip: "Report a listing using the flag icon on any item card. Reports are reviewed by admins.",
  },
  {
    id: "community",
    title: "Community & Chat",
    description:
      "Connect with other artists and fans via direct messages and group chats. Your contacts are people you follow or who follow you (Instagram-style). Admins see all users as potential contacts.",
    target: null,
    position: "center",
    icon: "💬",
    page: "/community",
    animation: "none",
    tip: "Chat rooms are real-time via Firestore listeners.",
  },
  {
    id: "radio",
    title: "AI Radio & DJ Studio",
    description:
      "The Radio section features curated stations and an AI DJ. The DJ Studio lets you create custom AI DJ personas with unique voices, wording styles, and character scripts that introduce tracks.",
    target: null,
    position: "center",
    icon: "📻",
    page: "/radio",
    animation: "none",
    tip: "Admin & Creator accounts can build and publish custom AI DJ personas.",
  },
  {
    id: "profile",
    title: "Your Profile",
    description:
      "Your profile is your public page — wall posts, your music, videos, marketplace listings, playlists, and social links. Use Wall Studio to customise background, colours, font, and pinned content blocks.",
    target: null,
    position: "center",
    icon: "👤",
    page: "/profile",
    animation: "none",
    tip: "Toggle 'View as Visitor' (👁 button) to preview how others see your profile.",
  },
  {
    id: "listening-party",
    title: "Listening Party",
    description:
      "Host a real-time synced listening session with friends. Create a party room, share the code, and everyone hears the same track at exactly the same moment — with NTP-style clock sync and acoustic calibration.",
    target: null,
    position: "center",
    icon: "🎉",
    page: "/party",
    animation: "none",
    tip: "Use the Acoustic Sync button to compensate for Bluetooth speaker latency.",
  },
  {
    id: "help",
    title: "You're All Set! 🎉",
    description:
      "That's a full tour of Breakin' Bread. You can revisit this tour any time from Profile → Help tab. If you have questions, the AI Support assistant can explain any feature and link you to the relevant section.",
    target: null,
    position: "center",
    icon: "✅",
    page: "profile/help",
    animation: "none",
  },
];

/** Group steps by page for the Help tab reference */
export const TOUR_BY_PAGE = TOUR_STEPS.reduce<Record<string, TourStep[]>>((acc, step) => {
  const p = step.page;
  if (!acc[p]) acc[p] = [];
  acc[p].push(step);
  return acc;
}, {});

// ─── Music Maker Tour ─────────────────────────────────────────────────────────
// Shown to creator / venue / admin / superadmin accounts.
// Focuses on uploading, releasing, selling, and analysing music.

export const MAKER_TOUR_STEPS: TourStep[] = [
  {
    id: "maker-welcome",
    title: "Welcome, Music Maker!",
    description:
      "Breakin' Bread is built for artists like you — upload your music, distribute releases, sell directly to fans, track your performance, and connect with your audience. Let's take a quick tour.",
    target: null,
    position: "center",
    icon: "🎤",
    page: "overview",
    animation: "none",
  },
  {
    id: "maker-sidebar",
    title: "Your Creator Hub",
    description:
      "The sidebar gives you access to every creator tool — your profile, media vault, releases, marketplace, stats, earnings, artwork studio, and more. Hover an icon to see its label.",
    target: "tour-sidebar",
    position: "right",
    icon: "🧭",
    page: "everywhere",
    animation: "pulse",
    tip: "Creator-only pages like Media Vault, Artwork Studio, and Earnings are hidden from consumer accounts.",
  },
  {
    id: "maker-profile",
    title: "Your Artist Profile",
    description:
      "Your profile is your public artist page — showcase your music, videos, marketplace listings, wall posts, and social links. Use Wall Studio to customise the look with themes, backgrounds, and pinned content blocks.",
    target: null,
    position: "center",
    icon: "👤",
    page: "/profile",
    animation: "none",
    tip: "Toggle 'View as Visitor' (👁 button) to preview exactly how fans see your page.",
  },
  {
    id: "maker-media-vault",
    title: "Media Vault",
    description:
      "Upload singles, full albums, or EPs with metadata, cover art, lyrics, credits, and pricing. Bulk-upload entire albums at once — AI can even extract credits from your artwork photos or typed liner notes.",
    target: null,
    position: "center",
    icon: "🗂️",
    page: "/media",
    animation: "none",
    tip: "Set each track as Free, Pay What You Want, or a fixed price. Stripe handles all payments.",
  },
  {
    id: "maker-releases",
    title: "Releases & Albums",
    description:
      "Group your tracks into albums, EPs, singles, or mixtapes. Releases appear on your artist page and in the home feed's Releases section. Each gets its own page with artwork, tracklist, and credits.",
    target: null,
    position: "center",
    icon: "💿",
    page: "/media",
    animation: "none",
    tip: "When you bulk-upload, a release doc is auto-created linking all uploaded tracks.",
  },
  {
    id: "maker-marketplace",
    title: "Marketplace",
    description:
      "List digital downloads, beats, samples, merchandise, and more. Set fixed prices or Name Your Price. Buyers pay via Stripe — funds go directly to you.",
    target: null,
    position: "center",
    icon: "🛍️",
    page: "/marketplace",
    animation: "none",
    tip: "Add and manage listings directly from your Profile page → Marketplace tab.",
  },
  {
    id: "maker-stats",
    title: "Stats & Analytics",
    description:
      "Track plays, downloads, follower growth, and marketplace revenue across all your content. See which tracks perform best and get insight into where your listeners are coming from.",
    target: null,
    position: "center",
    icon: "📊",
    page: "/stats",
    animation: "none",
  },
  {
    id: "maker-artwork",
    title: "Artwork Studio",
    description:
      "Design cover art, lyric cards, and promotional visuals inside the app. Generate AI artwork or upload your own — export at full resolution, ready for release.",
    target: null,
    position: "center",
    icon: "🎨",
    page: "/artwork-studio",
    animation: "none",
  },
  {
    id: "maker-radio",
    title: "AI Radio & DJ Personas",
    description:
      "Get your music on curated radio stations and build custom AI DJ personas with their own voice, style, and character scripts that introduce your tracks to listeners.",
    target: null,
    position: "center",
    icon: "📻",
    page: "/radio",
    animation: "none",
    tip: "Creator and Admin accounts can publish custom AI DJ personas to the platform.",
  },
  {
    id: "maker-party",
    title: "Listening Party",
    description:
      "Host real-time synced listening sessions with your fans. Share a party code, everyone hears the same track at exactly the same moment — with NTP clock sync and acoustic calibration for Bluetooth speakers.",
    target: null,
    position: "center",
    icon: "🎉",
    page: "/party",
    animation: "none",
    tip: "Perfect for album launch events, exclusive listening sessions, and fan engagement.",
  },
  {
    id: "maker-community",
    title: "Community & Chat",
    description:
      "Direct message fans and fellow creators. Your contacts include everyone who follows you and everyone you follow. Build relationships, collaborate, and stay connected with your audience.",
    target: null,
    position: "center",
    icon: "💬",
    page: "/community",
    animation: "none",
  },
  {
    id: "maker-done",
    title: "You're Ready to Create!",
    description:
      "That's your Breakin' Bread creator tour. Start by uploading your first track in the Media Vault, or set up your artist profile. Revisit this tour any time from Profile → Help.",
    target: null,
    position: "center",
    icon: "✅",
    page: "profile/help",
    animation: "none",
  },
];

// ─── Music Lover Tour ─────────────────────────────────────────────────────────
// Shown to consumer / consumer-business accounts.
// Focuses on discovering music, following artists, and connecting with community.

export const LOVER_TOUR_STEPS: TourStep[] = [
  {
    id: "lover-welcome",
    title: "Welcome, Music Lover!",
    description:
      "Breakin' Bread is your home for discovering new music, following independent artists, collecting releases, and connecting with a global community of music fans. Let's show you around.",
    target: null,
    position: "center",
    icon: "🎵",
    page: "overview",
    animation: "none",
  },
  {
    id: "lover-sidebar",
    title: "Navigate the Platform",
    description:
      "Everything lives in the sidebar — Home, Artists, Marketplace, Events, Radio, and your Profile. Click any icon to jump there instantly. On mobile, tap the ☰ button at the top-left to open the menu.",
    target: "tour-sidebar",
    position: "right",
    icon: "🧭",
    page: "everywhere",
    animation: "pulse",
    tip: "You can browse all public content without an account. Sign in to save favourites and unlock more.",
  },
  {
    id: "lover-home",
    title: "Home Feed",
    description:
      "Your home page surfaces featured artists, releases, trending tracks, upcoming events, and community picks — updated in real time. Use the filter tabs to jump between categories.",
    target: "tour-home-feed",
    position: "bottom",
    icon: "🏠",
    page: "/home",
    animation: "pulse",
    tip: "Switch between All, Following, and Suggested modes at the top to personalise what you see.",
  },
  {
    id: "lover-favorites",
    title: "Favourites Bar",
    description:
      "The scrollable strip at the top saves your favourite tracks, releases, artists, and videos for instant one-tap playback. Tap ♥ on anything to add it — favourites sync across all your devices.",
    target: "tour-favorites-bar",
    position: "bottom",
    icon: "❤️",
    page: "/home",
    animation: "pulse",
  },
  {
    id: "lover-player",
    title: "Media Player",
    description:
      "Hit ▶ on any track to start listening instantly. The global player at the bottom shows lyrics, waveform visualiser, and timeline comments from other listeners. Tap the artwork to open the full player.",
    target: "tour-media-player",
    position: "top",
    icon: "🎧",
    page: "everywhere",
    animation: "glow",
    tip: "Timeline comments are pinned to the playback position — leave your own reaction at any timestamp.",
  },
  {
    id: "lover-artists",
    title: "Discover Artists",
    description:
      "Browse the full artist directory — filter by genre, sort A-Z or by popularity, switch between grid and list views. Each artist page shows their full catalogue, gallery, videos, and curated web links.",
    target: null,
    position: "center",
    icon: "🎤",
    page: "/artists",
    animation: "none",
    tip: "Click Follow on any artist page to see their releases in your home feed.",
  },
  {
    id: "lover-marketplace",
    title: "Marketplace",
    description:
      "Buy digital downloads, beats, samples, and merch directly from independent artists. Items support fixed pricing or Name Your Price — you decide what you pay on PWYW releases.",
    target: null,
    position: "center",
    icon: "🛍️",
    page: "/marketplace",
    animation: "none",
  },
  {
    id: "lover-radio",
    title: "AI Radio — Always On",
    description:
      "Tune into curated stations 24/7, with an AI DJ that introduces each track in its own voice and personality. Great for background listening while you browse the rest of the platform.",
    target: null,
    position: "center",
    icon: "📻",
    page: "/radio",
    animation: "none",
  },
  {
    id: "lover-party",
    title: "Listening Party",
    description:
      "Join real-time synced listening sessions hosted by artists or friends. Everyone hears the same music at exactly the same moment — no matter where in the world they are.",
    target: null,
    position: "center",
    icon: "🎉",
    page: "/party",
    animation: "none",
    tip: "Use the Acoustic Sync button to calibrate for Bluetooth speaker latency.",
  },
  {
    id: "lover-community",
    title: "Community & Chat",
    description:
      "Message the artists you follow and connect with other fans. Your contacts include everyone you follow plus people who follow you back — Instagram-style mutual connections.",
    target: null,
    position: "center",
    icon: "💬",
    page: "/community",
    animation: "none",
  },
  {
    id: "lover-profile",
    title: "Your Profile",
    description:
      "Your profile is your personal space — wall posts, playlists, your collection, and social links. Customise it with Wall Studio themes, backgrounds, fonts, and pinned content blocks.",
    target: null,
    position: "center",
    icon: "👤",
    page: "/profile",
    animation: "none",
    tip: "Toggle 'View as Visitor' (👁 button) to see exactly how others see your profile.",
  },
  {
    id: "lover-done",
    title: "You're All Set!",
    description:
      "That's your Breakin' Bread music lover tour. Start by exploring the home feed or browsing artists. Revisit this tour any time from Profile → Help.",
    target: null,
    position: "center",
    icon: "✅",
    page: "profile/help",
    animation: "none",
  },
];

// ─── Guest / Logged-Out Tour ──────────────────────────────────────────────────
// Shown to visitors who aren't signed in. Focuses on the concept of the app,
// what they can browse freely, and why they should create an account.

export const GUEST_TOUR_STEPS: TourStep[] = [
  {
    id: "guest-welcome",
    title: "Welcome to Breakin' Bread",
    description:
      "Breakin' Bread is an all-in-one music platform built for artists, fans, and music lovers. Play music, discover emerging artists, buy and sell in the marketplace, and connect with a global community — all in one place.",
    target: null,
    position: "center",
    icon: "🍞",
    page: "overview",
    animation: "none",
  },
  {
    id: "guest-sidebar",
    title: "Everything is Right Here",
    description:
      "Use the sidebar to navigate the whole platform. Browse Artists, discover new Music, explore the Marketplace, tune into AI Radio, and more — all without needing an account. Just click any icon to explore.",
    target: "tour-sidebar",
    position: "right",
    icon: "🧭",
    page: "everywhere",
    animation: "pulse",
    tip: "You can browse all public content as a guest. Create an account to save favourites and unlock more.",
  },
  {
    id: "guest-home",
    title: "Discover What's New",
    description:
      "The home page surfaces featured artists, releases, trending tracks, upcoming events, and community picks. Everything updates in real time — there's always something fresh to discover.",
    target: "tour-home-feed",
    position: "bottom",
    icon: "🏠",
    page: "/home",
    animation: "pulse",
  },
  {
    id: "guest-favorites",
    title: "The Favourites Bar",
    description:
      "The scrollable bar at the top of the page shows your saved tracks, releases, artists, and videos for instant playback. Tap ♥ on anything to add it. Sign in to sync your favourites across every device.",
    target: "tour-favorites-bar",
    position: "bottom",
    icon: "❤️",
    page: "/home",
    animation: "pulse",
    tip: "You can heart tracks as a guest, but they'll only persist on this device until you sign in.",
  },
  {
    id: "guest-player",
    title: "Play Music Instantly",
    description:
      "Hit ▶ on any track to start listening — no account needed for public content. The global player at the bottom lets you control playback, see lyrics, view the waveform, and read timeline comments from other listeners.",
    target: "tour-media-player",
    position: "top",
    icon: "🎧",
    page: "everywhere",
    animation: "glow",
  },
  {
    id: "guest-themes",
    title: "Customise Your Experience",
    description:
      "Breakin' Bread has dozens of themes — Hip-Hop gold, Classic Jazz, Reggae greens, country flag colours, GIF backgrounds, and more. Open the theme picker from the sidebar to switch the whole app's look in one click.",
    target: null,
    position: "center",
    icon: "🎨",
    page: "everywhere",
    animation: "none",
    tip: "Click the same country flag theme twice to rotate through different colour-swap variations.",
  },
  {
    id: "guest-radio",
    title: "AI Radio — Always On",
    description:
      "The Radio section plays curated stations 24/7, with an AI DJ that introduces each track in its own voice and personality. Perfect for background listening while you explore the platform.",
    target: null,
    position: "center",
    icon: "📻",
    page: "/radio",
    animation: "none",
  },
  {
    id: "guest-signin",
    title: "Unlock the Full Platform",
    description:
      "Creating a free account gives you a personal profile, media vault to upload and sell your music, access to community chat, listening parties, and full favourites sync. Artists get even more — releases, marketplace, and AI credits.",
    target: null,
    position: "center",
    icon: "🔓",
    page: "overview",
    animation: "none",
    cta: { label: "Sign In / Create Account", href: "/signin" },
  },
];
